'use client';

import { useRouter } from 'next/navigation';
import type { RelatedEvent } from '@/types/eventDetail';
import { EventCard } from '../../molecules/EventCard';

export interface SimilarEventsSectionProps {
  relatedEvents: RelatedEvent[];
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getVenueLabel(event: RelatedEvent): string {
  if (event.location) {
    const parts = [event.location.name, event.location.city].filter(Boolean);
    if (parts.length > 0) return parts.join(', ');
  }
  return 'Venue TBA';
}

function getPriceLabel(event: RelatedEvent): string | undefined {
  if (!event.price) return undefined;
  return event.price;
}

function getPrimaryCategory(event: RelatedEvent): string {
  return event.categories[0] ?? 'Event';
}

function RelatedEventCard({
  event,
  fullWidth,
  className,
  wrapperClassName,
}: {
  event: RelatedEvent;
  fullWidth?: boolean;
  className?: string;
  wrapperClassName?: string;
}) {
  const router = useRouter();
  const price = getPriceLabel(event);
  const venueWithPrice = price ? `${getVenueLabel(event)} · ${price}` : getVenueLabel(event);

  return (
    <div className={wrapperClassName}>
      <EventCard
        title={event.title}
        imageUrl={event.imageUrl ?? undefined}
        category={getPrimaryCategory(event)}
        date={formatDate(event.date)}
        venue={venueWithPrice}
        fullWidth={fullWidth}
        className={className}
        onCtaClick={() => router.push(`/event/${event.slug ?? event.id}`)}
      />
    </div>
  );
}

export function SimilarEventsSection({ relatedEvents }: SimilarEventsSectionProps) {
  if (!relatedEvents.length) return null;

  return (
    <section aria-labelledby="similar-events-heading" data-testid="similar-events-section">
      <h2 id="similar-events-heading" className="text-lg font-semibold mb-4 font-outfit">
        You Might Also Like
      </h2>

      <div className="block tablet:hidden -mx-4" data-testid="similar-events-mobile-rail">
        <div className="w-full overflow-x-auto pb-4 snap-x snap-mandatory flex gap-4 px-4 scrollbar-hide">
          {relatedEvents.map((event) => (
            <RelatedEventCard
              key={event.id}
              event={event}
              className="w-[280px] min-h-[400px]"
              wrapperClassName="snap-center shrink-0"
            />
          ))}
          <div className="w-4 shrink-0" />
        </div>
      </div>

      <div
        className="hidden tablet:grid grid-cols-2 desktop:grid-cols-3 gap-5 wide:gap-6"
        data-testid="similar-events-desktop-grid"
      >
        {relatedEvents.map((event) => (
          <RelatedEventCard key={event.id} event={event} fullWidth className="h-full" />
        ))}
      </div>
    </section>
  );
}
